import { useFetcher } from '@remix-run/react'
import { UploadIcon } from 'lucide-react'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { MediaManagerContext } from '~/contexts'
import { Spinner } from '~/ui'
import { cn } from '~/utils/classnames'

interface AvatarUploadProps {
  ref?: React.Ref<HTMLDivElement>
  avatarSrc?: string | null
  className?: string
  uploadAction?: string | null
  onUploadSuccess?: (avatarSrc: string) => void
}

/**
 * This component is meant to be used within an Avatar (from ui/Avatar).
 */
export function AvatarUpload({
  ref,
  avatarSrc,
  className,
  uploadAction,
  onUploadSuccess,
}: AvatarUploadProps) {
  const fetcher = useFetcher()
  const { t } = useTranslation()
  const [, setMediaManagerContext] = useContext(MediaManagerContext)

  const handleClick = () => {
    setMediaManagerContext({
      isOpen: true,
      intent: onUploadSuccess ? 'return_attachments' : 'upload_attachments',
      maxSelected: 1,
      title: t('action.select_avatar'),
      targetType: 'avatar',
      allowedMediaKinds: ['image'],
      onAttachmentsComplete: (attachments) => {
        if (attachments[0]) {
          if (onUploadSuccess) {
            onUploadSuccess(attachments[0].media.src)
            return
          }

          if (uploadAction) {
            fetcher.submit(
              { avatarSrc: attachments[0].media.src },
              { method: 'PATCH', action: uploadAction }
            )
          }
        }
      },
    })
  }

  const isUploadingAvatar = fetcher.state !== 'idle'

  return (
    <div
      ref={ref}
      className={cn(
        'group absolute -inset-px z-10',
        'hover:border-accent-element ring-accent-element border border-transparent ring-0 transition-all hover:ring-4',
        'cursor-pointer overflow-hidden rounded-[inherit]',
        isUploadingAvatar && 'pointer-events-none',
        className
      )}
      onClick={handleClick}
    >
      <div
        className={cn(
          'absolute inset-0 z-20 flex items-center justify-center bg-black/50 text-white transition-opacity group-hover:opacity-100',
          avatarSrc ? 'opacity-0' : 'opacity-40'
        )}
        inert
      >
        <UploadIcon
          className={cn(
            'transition-transform duration-100 ease-out group-hover:translate-y-0',
            avatarSrc ? 'translate-y-1' : 'size-4'
          )}
        />
      </div>

      {isUploadingAvatar && (
        <div
          className="bg-under/20 text-highlight absolute inset-0 flex items-center justify-center"
          inert
        >
          <Spinner />
        </div>
      )}
    </div>
  )
}
